import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ShoppingBag, HelpCircle } from "lucide-react";
import { getResourcePage, RESOURCE_PAGES } from "@/data/seoPages";
import { getArticle } from "@/data/blog";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";

export default function ResourcePage({ slug }) {
  const page = getResourcePage(slug);
  const navigate = useNavigate();

  useSeo({
    title: page ? page.title : "Ozentya",
    description: page ? page.description : "",
    path: page ? page.path : "/",
  });

  useJsonLd(
    "ld-resource",
    page
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              name: page.title,
              description: page.description,
              url: `${SITE_URL}${page.path}`,
              inLanguage: "fr-FR",
              isPartOf: { "@type": "WebSite", name: "Ozentya", url: SITE_URL },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
                { "@type": "ListItem", position: 2, name: page.breadcrumb, item: `${SITE_URL}${page.path}` },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: (page.faq || []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }
      : null
  );

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const goToPacks = () => {
    navigate("/packs");
    setTimeout(() => document.getElementById("packs")?.scrollIntoView({ behavior: "smooth" }), 120);
  };

  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">Page introuvable</h1>
        <Link to="/" className="text-[var(--oz-gold)] font-semibold">Retour à l'accueil</Link>
      </div>
    );
  }

  const related = (page.related || []).map((s) => RESOURCE_PAGES[s]).filter(Boolean);
  const articles = (page.articles || []).map(getArticle).filter(Boolean);

  return (
    <div data-testid={`resource-${page.slug}`} className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-12 md:py-16">
      {/* Fil d'ariane */}
      <nav aria-label="Fil d'ariane" className="text-xs text-[var(--oz-muted)] mb-6">
        <Link to="/" className="inline-block py-2 hover:text-[var(--oz-text)]">Accueil</Link>
        <span className="mx-2">/</span>
        <span className="text-[var(--oz-text)]">{page.breadcrumb}</span>
      </nav>

      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[var(--oz-text)] leading-tight">
        {page.h1}
      </h1>

      {/* Réponse directe (pour Google & moteurs IA) */}
      <p className="mt-6 text-lg text-[var(--oz-text)] leading-relaxed bg-[var(--oz-bg-2)] rounded-2xl p-5 border border-[var(--oz-border)]">
        {page.intro}
      </p>

      <article className="mt-10 space-y-9">
        {page.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)] mb-3">{s.h}</h2>
            {s.p && <p className="text-base text-[var(--oz-muted)] leading-relaxed">{s.p}</p>}
            {s.list && (
              <ul className="mt-3 space-y-2">
                {s.list.map((li, j) => (
                  <li key={j} className="flex gap-2.5 text-base text-[var(--oz-muted)] leading-relaxed">
                    <Check className="w-5 h-5 text-[var(--oz-gold)] flex-shrink-0 mt-0.5" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>

      {/* CTA vers les packs */}
      <div className="mt-12 rounded-[1.5rem] bg-[var(--oz-dark)] text-white p-8 text-center">
        <h2 className="font-serif text-2xl sm:text-3xl text-white">Prêt(e) à développer votre complément de revenu ?</h2>
        <p className="mt-3 text-sm text-white/70 max-w-xl mx-auto">
          Découvrez les Packs Pro Ozentya : recevez vos produits, présentez-les et revendez-les à votre rythme.
          Aucun revenu n'est garanti — vos gains dépendent des ventes réalisées.
        </p>
        <button
          data-testid={`resource-cta-${page.slug}`}
          onClick={goToPacks}
          className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold text-sm hover:brightness-95 transition-all"
        >
          <ShoppingBag className="w-4 h-4" /> Découvrir les packs <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* FAQ */}
      {page.faq && page.faq.length > 0 && (
        <div className="mt-14">
          <h2 className="font-serif text-2xl sm:text-3xl text-[var(--oz-text)] mb-6 flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-[var(--oz-gold)]" /> Questions fréquentes
          </h2>
          <div className="space-y-4">
            {page.faq.map((f, i) => (
              <div key={i} className="bg-white border border-[var(--oz-border)] rounded-2xl p-5">
                <h3 className="font-semibold text-[var(--oz-text)]">{f.q}</h3>
                <p className="mt-2 text-sm text-[var(--oz-muted)] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Maillage interne : pages liées */}
      {related.length > 0 && (
        <div className="mt-14 pt-10 border-t border-[var(--oz-border)]">
          <h2 className="font-serif text-2xl text-[var(--oz-text)] mb-5">Pour aller plus loin</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={r.path}
                className="group flex items-center justify-between gap-3 bg-white border border-[var(--oz-border)] rounded-xl px-5 py-4 hover:border-[var(--oz-gold)] transition-colors"
              >
                <span className="font-semibold text-[var(--oz-text)]">{r.breadcrumb}</span>
                <ArrowRight className="w-4 h-4 text-[var(--oz-gold)] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Maillage interne : articles liés */}
      {articles.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-[var(--oz-text)] mb-5">Nos guides sur le sujet</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {articles.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="group flex gap-4 items-center">
                <img src={a.cover} alt={a.title} loading="lazy" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                <span className="text-sm font-semibold text-[var(--oz-text)] group-hover:text-[var(--oz-gold)] transition-colors leading-snug">{a.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
